var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { Text } from '@inter-ikea-kompis/component-text';
import CountdownStyle from './CountdownStyle.js';
const COUNTDOWN_HEIGHT = 56;
/**
 * This component should be used for general Countdown and it provides with general stylings according to NCL and fixes for common browsers and touch devices.
 *
 * @event timeEnd When the animation for circle tick ends.
 * @event timeChange When time changes.
 */
export default class Countdown extends KompisElement {
    constructor() {
        super(...arguments);
        // Start value for countdown
        this.startTime = 0;
        // Current value for countdown.
        this.time = 0;
        // Radius and size of SVG circle.
        this.radius = 0;
        this.circumference = 0;
        // Timeout
        this.timeout = null;
        this.circleAnimation = false;
    }
    /**
     * Renders the component.
     *
     * @returns Template.
     */
    render() {
        const scale = this.getScale();
        // We need to calculate the size and radius for the SVG.
        this.radius = (COUNTDOWN_HEIGHT * scale) / 2;
        this.circumference = 2 * Math.PI * this.radius;
        return html `
			<style>
				${CountdownStyle.getStyle({
            theme: this.theme,
            circleAnimation: this.circleAnimation
        })}
			</style>
			<svg>
				<text x="50%" y="53%" dominant-baseline="middle" text-anchor="middle">${this.time}</text>
				<circle class="circle--bg" cx="50%" cy="50%" r="49%" />
				<circle
					cx="50%"
					cy="50%"
					r="49%"
					stroke-dasharray="${this.getStrokeDasharray()},${this.circumference -
            this.getStrokeDasharray()}"
					stroke-dashoffset="${this.getStrokeOffset()}"
				></circle>
			</svg>
		`;
    }
    connectedCallback() {
        this.customElements.define('kompis-text', Text);
        super.connectedCallback();
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.timeout) {
            clearTimeout(this.timeout);
        }
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('time')) {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            if (this.time < this.startTime) {
                this.circleAnimation = true;
            }
            if (this.time === 0) {
                this.circleAnimation = false;
                this.dispatchEvent(new CustomEvent('timeEnd', {
                    bubbles: true,
                    detail: {
                        visible: false
                    }
                }));
            }
            else {
                this.timeout = setTimeout(() => {
                    this.dispatchEvent(new CustomEvent('timeChange', {
                        bubbles: true,
                        detail: {
                            time: this.time - 1
                        }
                    }));
                }, 1000);
            }
        }
        return true;
    }
    /**
     * Returns the scale calculated from the rem-scaling.
     */
    getScale() {
        const rootFontSize = window
            .getComputedStyle(document.documentElement)
            .getPropertyValue('font-size');
        let scale = 0;
        if (rootFontSize) {
            if (rootFontSize.includes('%')) {
                scale = Number(rootFontSize.replace('%', '')) / 100;
            }
            else {
                scale = Number(rootFontSize.replace('px', '')) / 16;
            }
        }
        return scale;
    }
    /**
     * First, 1/4 of circumfence of 90 degrees. To start from top of the view,
     * we must rotate it by 90 degrees.
     * By default circle will start on the right.
     * Stroke offset effectively rotates the circle.
     */
    getStrokeOffset() {
        return (1 / 4) * this.circumference;
    }
    /**
     * Second, calculate dash array. We need dash array containing only two parts -
     * visible dash, and invisible dash.
     * Visible dash should have length of the chosen angle (time). Full circle is 360 degrees (startTime),
     * and this 360 degrees does also equal the entire circumference. We want just a part of
     * this entire circle to be visible - (time / startTime) returns a percentage value
     * (between 0.0 and 100.0) of how much circumference should be visible.
     * Hence, we then multiply (time / startTime) times the entire circumference.
     */
    getStrokeDasharray() {
        return (this.time / this.startTime) * this.circumference;
    }
}
__decorate([
    property({ type: Number })
], Countdown.prototype, "startTime", void 0);
__decorate([
    property({ type: Number })
], Countdown.prototype, "time", void 0);
__decorate([
    property({ type: Object, required: true })
], Countdown.prototype, "theme", void 0);
//# sourceMappingURL=Countdown.js.map